/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { TS_FORMAT, TIME_GRAINS } from 'constants';

import { defineMessages } from 'react-intl';
import omit from 'lodash/omit';
import merge from 'lodash/merge';
import values from 'lodash/values';
import keyBy from 'lodash/keyBy';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import {
  fetchGet,
  fetchPost,
  fetchPut,
  fetchDelete,
  multipartFormHeaders,
} from '../../utils/FetchAPIs';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { calculateLocalImage, I18NError } from 'Utils';
import { RESOURCE_TYPES } from 'dataConstants';

const dashboardBaseURL = '/api/dashboard';
const dashboardDataURL = '/api/asengine';

const messages = defineMessages({
  errorDeletingDashboard: 'Error deleting the dashboard. {message}',
  errorUpdatingDashboard: 'Error updating the dashboard. {message}',
  errorCreatingDashboard: 'Error creating the dashboard. {message}',
  errorUploadingImage: 'Error uploading the image. {message}',
  errorReplacingImage: 'Error replacing the image. {message}',
});

/** returns valid dashboard templates for an entity type */
export const getDashboardTemplates = entityType =>
  fetchGet(`${dashboardBaseURL}/assetTypes/${entityType}/templates`);

/**
 * Return the template for the entity Type passed
 * @param {*} entityType
 * @param {*} templateName
 * @returns
 */
export const getDashboardTemplate = (entityType, templateName) =>
  fetchGet(`${dashboardBaseURL}/assetTypes/${entityType}/templates/${templateName}`);

/** returns a single instance dashboard for an entityType */
export const getInstanceDashboard = (entityType, entityId, dashboardId) =>
  fetchGet(
    `${dashboardBaseURL}/assetTypes/${entityType}/assets/${entityId}/dashboards/${dashboardId}`
  );

/**
 * return Device type or device level dashboards. By default it returns device level dashboards
 * @param {*} deviceTypeName Name of the device type
 * @param {*} deviceId Name of the device
 * @param {*} type Type of the dashboard. example instance dashboard (DEVICE) or Device type level dashboard (DEVICE_TYPE)
 * @returns
 */
export const getDashboardsV1API = (deviceTypeName, deviceId, type = RESOURCE_TYPES.DEVICE) => {
  const dashboardGetURL =
    type === RESOURCE_TYPES.DEVICE_TYPE
      ? `${dashboardBaseURL}/assetTypes/${deviceTypeName}`
      : deviceId
      ? `${dashboardBaseURL}/assetTypes/${deviceTypeName}/assets/${deviceId}`
      : `${dashboardBaseURL}/assetTypes/${deviceTypeName}/assets`; // <-- the following rest URL not present in the backend so it wont really work.

  return fetchGet(dashboardGetURL).then(response =>
    Array.isArray(response)
      ? response
          .filter(dashboard => dashboard.type && dashboard.type === type)
          // adding the entityId so I can make sure the current dashboards are valid for a given entity type
          .map(dashboard => ({ ...dashboard, entityType: deviceTypeName, entityId: deviceId }))
      : []
  );
};
/**
 * API to delete the dashboard at device type or device level.
 * @param {string} deviceTypeName name of the device type
 * @param {string} deviceId name of the device
 * @param {*} dashboardMetaId dashboard meta data id
 * @param {string} userEmailId email id of the person who is deleting the dashboard
 * @returns
 */
export const deleteDashboardV1API = (deviceTypeName, deviceId, dashboardMetaId, userEmailId) =>
  fetchDelete(
    deviceId
      ? `${dashboardBaseURL}/assetTypes/${deviceTypeName}/assets/${deviceId}/dashboards/${dashboardMetaId}`
      : `${dashboardBaseURL}/assetTypes/${deviceTypeName}/dashboards/${dashboardMetaId}`,
    null,
    { userEmailId }
  ).catch(error => {
    throw new I18NError(messages.errorDeletingDashboard, {
      message: formatErrorResponse(error),
    });
  });

export const createDashboardV1API = (entityType, entityId, dashboard, userEmailId) => {
  const dashboardCreateURL =
    dashboard?.type === RESOURCE_TYPES.DEVICE_TYPE
      ? `${dashboardBaseURL}/assetTypes/${entityType}`
      : entityId
      ? `${dashboardBaseURL}/assetTypes/${entityType}/assets/${entityId}`
      : `${dashboardBaseURL}/assetTypes/${entityType}/assets`;

  return fetchPost(
    dashboardCreateURL,
    {
      ...dashboard,
      // TODO: move this to the caller
      dashboardMetadataJson: omit(dashboard.dashboardMetadataJson, [
        '_id',
        '_rev',
        'entitytype_id',
        'tenantId',
      ]),
    },
    { userEmailId }
  ).catch(error => {
    throw new I18NError(messages.errorCreatingDashboard, {
      message: formatErrorResponse(error),
    });
  });
};

export const updateInstanceDashboard = (entityType, entityId, dashboard, userEmailId) =>
  fetchPut(
    `${dashboardBaseURL}/assetTypes/${entityType}/assets/${entityId}/dashboards/${dashboard.dashboardMetaId}`,
    {
      ...dashboard,
      // TODO: move this to the caller
      dashboardMetadataJson: omit(dashboard.dashboardMetadataJson, [
        '_id',
        '_rev',
        'entitytype_id',
        'tenantId',
      ]),
    },
    { userEmailId }
  ).catch(error => {
    throw new I18NError(messages.errorUpdatingDashboard, {
      message: formatErrorResponse(error),
    });
  });

/**
 * API to Updates the sequence of the dashboard
 * @param {string} deviceTypeName name of the device type
 * @param {string} deviceId name of the device
 * @param {*} dashboardMetaId dashboard meta data id
 * @param {*} sequenceNumber the sequence number
 * @returns
 */
export const updateDashboardSequenceV1API = (
  deviceTypeName,
  deviceId,
  dashboardMetaId,
  sequenceNumber,
  userEmailId
) =>
  fetchPut(
    deviceId
      ? `${dashboardBaseURL}/assetTypes/${deviceTypeName}/assets/${deviceId}/dashboards/${dashboardMetaId}/sequence`
      : `${dashboardBaseURL}/assetTypes/${deviceTypeName}/dashboards/${dashboardMetaId}/sequence`,
    {},
    { userEmailId },
    { sequenceNumber }
  );

/**
 *  API to enables or disables an dashboard
 * @param {string} deviceTypeName name of the device type
 * @param {string} deviceId name of the device
 * @param {*} dashboardMetaId dashboard meta data id
 * @param {boolean} isEnabled on true dashboard will disbaled
 * @returns
 */
export function updateDashboardEnabledStatusV1API(
  deviceTypeName,
  deviceId,
  dashboardMetaId,
  isEnabled,
  userEmailId
) {
  return fetchPut(
    deviceId
      ? `${dashboardBaseURL}/assetTypes/${deviceTypeName}/assets/${deviceId}/dashboards/${dashboardMetaId}/status`
      : `${dashboardBaseURL}/assetTypes/${deviceTypeName}/dashboards/${dashboardMetaId}/status`,
    {},
    { userEmailId },
    { status: isEnabled }
  );
}

/**
 *
 * @param {object} agg_dict Attributes is an object of attribute names and the functions to apply { input_flow_rate: 'mean', pressure: ['min', 'max'] }
 * @param {object} agg_outputs An object of attribute names and the unique ids they should map to in the response
 * @param {array} raw_attributes An array of attributes to return as raw data
 * @param {object} raw_outputs A map to alias the output fields
 * @param {string} entityId (optional): entity id to filter on
 * @param {string} entityType required entity type to search
 * @param {object} startRange a moment object that indicates the start range
 * @param {object} endRange a moment object that indicates the end range
 * @param {string} timeGrain (optional) 'day','hour', doesn't apply to raw request
 * @param {array} groupBy (optional) array of column names to group by, doesn't apply to raw request
 * @param {object} dimensionFilter (optional) object with dimensional values to use for filtration, doesn't apply to raw request
 */
export const getLegacyDashboardCardData = (
  agg_dict, // eslint-disable-line @typescript-eslint/naming-convention
  agg_outputs, // eslint-disable-line @typescript-eslint/naming-convention
  raw_attributes, // eslint-disable-line @typescript-eslint/naming-convention
  raw_outputs, // eslint-disable-line @typescript-eslint/naming-convention
  entityId,
  entityType,
  startRange, // assumes moment
  endRange, // assumes moment
  timeGrain,
  groupBy,
  dimensionFilter,
  rawDataLimit,
  sampleRawData = false
) => {
  const promises = [];

  // First fetch raw data
  if (!isNil(raw_attributes) && !isEmpty(raw_attributes)) {
    promises.push(
      fetchPost(`${dashboardDataURL}/entity/type/${entityType}/queryrawdata`, {
        raw_attributes,
        output_map: raw_outputs,
        ...(entityId ? { entities: [entityId] } : {}),
        ...(startRange ? { start_ts: startRange.utc().format(TS_FORMAT) } : {}),
        ...(endRange ? { end_ts: endRange.utc().format(TS_FORMAT) } : {}),
        ...(dimensionFilter ? { dimension_filter: dimensionFilter } : {}),
        limit: rawDataLimit,
        sample: sampleRawData,
      })
    );
  }

  if (!isNil(agg_dict) && !isEmpty(agg_dict)) {
    // Now fetch aggregated data
    promises.push(
      fetchPost(`${dashboardDataURL}/entity/type/${entityType}/querydata`, {
        agg_dict,
        agg_outputs,
        ...(startRange ? { start_ts: startRange.utc().format(TS_FORMAT) } : {}),
        ...(endRange ? { end_ts: endRange.utc().format(TS_FORMAT) } : {}),
        ...(entityId ? { entities: [entityId] } : {}),
        ...(timeGrain ? { time_grain: timeGrain === TIME_GRAINS.HOUR ? '1H' : timeGrain } : {}),
        ...(groupBy ? { groupby: groupBy } : {}),
        ...(dimensionFilter ? { dimension_filter: dimensionFilter } : {}),
        // we have to send along the browser timezone so the aggregates use the correct bucket offsets
        // time_zone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
        limit: rawDataLimit,
      }).then(data =>
        isNil(timeGrain) && isNil(groupBy) && !isNil(data) && Array.isArray(data)
          ? // TODO: this is a workaround because the data layer is giving back these records as separate for each field
            [
              data.reduce((updatedData, dataPoint) => {
                updatedData = merge({}, dataPoint, updatedData); // eslint-disable-line no-param-reassign
                return updatedData;
              }, {}),
            ]
          : data
      )
    );
  }

  return Promise.all(promises).then(([rawResponse = [], aggregatedResponse = []]) => {
    // Special case where we merge on timestamp to support charts/tables with both raw and aggregate on the same graph. TODO: do we still need to do this?
    if (
      entityId && // only entityIds have raw metrics
      isNil(groupBy) && // no group by, would have multiple rows
      isNil(dimensionFilter) && // no dimension filter (means no summary dashboard too)
      !isNil(rawResponse) &&
      !isEmpty(rawResponse) &&
      !isNil(aggregatedResponse) &&
      !isEmpty(aggregatedResponse)
    ) {
      // if both the raw and aggregated response has a timestamp column, merge on it
      if (
        aggregatedResponse.some(aggData => aggData.timestamp) &&
        rawResponse.some(rawData => rawData.timestamp)
      ) {
        const merged = merge(
          keyBy(rawResponse, 'timestamp'),
          keyBy(aggregatedResponse, 'timestamp')
        );
        return values(merged);
      }
      if (aggregatedResponse.length === 1 && rawResponse.length === 1) {
        // in this case we have a simple value card with both raw and aggregate metrics and need to just merge the simple raw and aggregate values into the same record
        return [{ ...aggregatedResponse[0], ...rawResponse[0] }];
      }
    }

    // Otherwise just return either or both
    return [
      ...(Array.isArray(rawResponse) ? rawResponse : [rawResponse]),
      ...(Array.isArray(aggregatedResponse) ? aggregatedResponse : [aggregatedResponse]),
    ];
  });
};

/**
 * Creates a new image
 * @param {*} entityType entityType
 * @param {*} entity entity
 * @param {object} image {imageid: the image symbolic id, file: the image file}
 * @param {*} userEmailId
 */
export const uploadImage = (entityType, entity, image, userEmailId) => {
  const imageURL = isNil(entity)
    ? `/api/images/entityType/${entityType}`
    : `/api/images/entityType/${entityType}/entityid/${entity}`;
  const fileForm = new FormData();
  fileForm.set('imageid', image.id);
  fileForm.set('imagefilecontent', image.file);
  return fetchPost(imageURL, fileForm, {
    headers: multipartFormHeaders,
    userEmailId,
  }).catch(error => {
    throw new I18NError(messages.errorUploadingImage, {
      message: error?.response?.error || formatErrorResponse(error),
    });
  });
};

/**
 * Deletes the current image
 * @param {string} entityType
 * @param {string} entity
 * @param {string} imageid
 */
export const deleteImage = (entityType, entity, imageid, userEmailId) => {
  const imageURL = isNil(entity)
    ? `/api/images/entityType/${entityType}`
    : `/api/images/entityType/${entityType}/entityid/${entity}`;
  return fetchDelete(imageURL, null, { userEmailId }, { imageid });
};

/**
 * Returns image starts looking at the entity level then fails over to the EntityType level
 * @param {string} entityType
 * @param {string} entity
 * @param {string} imageId
 */
export const getImage = (entityType, entity, imageId) => {
  if (!isNil(entity)) {
    return fetchGet(`/api/images/entityType/${entityType}/entityid/${entity}/imageid/${imageId}`)
      .then(
        // First check for an entityImage, then an entityTypeImage
        entityImage =>
          entityImage?.blob
            ? entityImage?.blob()
            : fetchGet(`/api/images/entityType/${entityType}/imageid/${imageId}`).then(
                entityTypeImage => {
                  if (!entityTypeImage?.blob) {
                    throw new Error(
                      `No image found for entityType ${entityType} with id '${imageId}.'`
                    );
                  }
                  return entityTypeImage?.blob && entityTypeImage?.blob();
                }
              )
      )
      .then(blob => (blob ? URL.createObjectURL(blob) : undefined));
  }
  return fetchGet(`/api/images/entityType/${entityType}/imageid/${imageId}`)
    .then(entityTypeImage => {
      if (!entityTypeImage?.blob) {
        throw new Error(`No image found for entityType ${entityType} with id '${imageId}.'`);
      }
      return entityTypeImage?.blob && entityTypeImage?.blob();
    })
    .then(blob => (blob ? URL.createObjectURL(blob) : undefined));
};

/**
 * Returns a promise that will resolve to an array of images from the API layer
 * @param {string} deviceTypeName
 * @param {string} deviceId
 * @param {string[]} imageIdsFilter
 */
export const getImages = (deviceTypeName, deviceId = null, imageIdsFilter = []) =>
  fetchGet(`/api/images/entityType/${deviceTypeName}`, null, { all_devices: true }).then(
    response => {
      /**
       * This function retrun the images specific to entity.
       * Also override any entity type level image which has same imageid as that of entity
       */
      const getDeviceLevelImageOverride = () =>
        Object.values(
          response?.data
            .filter(image => image.entityid === null || (deviceId && deviceId === image.entityid))
            .reduce((acc, obj) => {
              const { imageid, entityid } = obj;
              if (acc[imageid] && entityid && acc[imageid].entityid == null) {
                acc[imageid] = obj; // device level image override
                return acc;
              }

              acc[imageid] = obj;
              return acc;
            }, {})
        );

      if (Array.isArray(response?.data)) {
        const mergedObj = getDeviceLevelImageOverride();

        return Promise.all(
          mergedObj
            .filter(({ imageid }) => isEmpty(imageIdsFilter) || imageIdsFilter.includes(imageid)) // if there is no imageId Filters defined load everything
            .map(({ imageid, imagefilename, entityid }) => {
              const imageURL = isNil(entityid)
                ? `/api/images/entityType/${deviceTypeName}` // device type level image
                : `/api/images/entityType/${deviceTypeName}/entityid/${entityid}`; // device level image
              return fetchGet(`${imageURL}/imageid/${imageid}`).then(eachImage =>
                calculateLocalImage(imageid, imagefilename, eachImage, deviceTypeName, entityid)
              );
            })
        );
      }
      return [];
    }
  );
